import { useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AddParticipantToCall from "./AddParticipantToCall";
import { useVideoRoom } from "./useVideoRoom";
import { useToast } from "../../../hooks/use-toast";

export default function TwillioVideoComponent({
  toggleFormsPanel,
  addparticipant,
  appointmentData,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.userReducer.user);
  const appointmentId = location.state?.appointmentId;
  const { toast } = useToast();

  // Use the custom video room hook
  const {
    localRef,
    remoteVideoElementsRef,
    isLoading,
    error,
    isAudioEnabled,
    isVideoEnabled,
    isLocalVideoAttached,
    remoteParticipants,
    maxParticipants,
    toggleAudio,
    toggleVideo,
    leaveRoom,
    retryConnection,
  } = useVideoRoom({
    appointmentId,
    userType: user?.userType,
    appointmentData,
    toast,
  });

  // Memoize display names to prevent unnecessary re-renders
  const displayNames = useMemo(() => {
    if (!appointmentData) {
      return {
        localName: user?.userType === "doctor" ? "Doctor" : "Patient",
        remoteName: user?.userType === "doctor" ? "Patient" : "Doctor",
        localInitials: user?.userType === "doctor" ? "DR" : "PT",
        remoteInitials: user?.userType === "doctor" ? "PT" : "DR",
      };
    }

    if (user?.userType === "doctor") {
      const patientName =
        `${appointmentData.patient?.firstName || "Patient"} ${appointmentData.patient?.lastName || ""}`.trim();
      const doctorName =
        `Dr. ${appointmentData.doctor?.firstName || "Doctor"} ${appointmentData.doctor?.lastName || ""}`.trim();
      return {
        localName: doctorName,
        remoteName: patientName,
        localInitials: `${appointmentData.doctor?.firstName?.[0] || "D"}${appointmentData.doctor?.lastName?.[0] || "R"}`,
        remoteInitials: `${appointmentData.patient?.firstName?.[0] || "P"}${appointmentData.patient?.lastName?.[0] || "T"}`,
      };
    } else {
      const patientName =
        `${appointmentData.patient?.firstName || "Patient"} ${appointmentData.patient?.lastName || ""}`.trim();
      const doctorName =
        `Dr. ${appointmentData.doctor?.firstName || "Doctor"} ${appointmentData.doctor?.lastName || ""}`.trim();
      return {
        localName: patientName,
        remoteName: doctorName,
        localInitials: `${appointmentData.patient?.firstName?.[0] || "P"}${appointmentData.patient?.lastName?.[0] || "T"}`,
        remoteInitials: `${appointmentData.doctor?.firstName?.[0] || "D"}${appointmentData.doctor?.lastName?.[0] || "R"}`,
      };
    }
  }, [appointmentData, user?.userType]);

  const { localName, localInitials } = displayNames;

  const handleLeaveCall = () => {
    console.log("🚪 User initiated leave call");

    // Clean up the room and tracks
    leaveRoom();

    // Add a small delay to ensure cleanup completes before navigation
    setTimeout(() => {
      if (user?.userType === "doctor") {
        navigate("/doctor/appointments-details", { state: { appointmentId } });
      } else if (user?.userType === "patient") {
        navigate("/patient/appointments-details", { state: { appointmentId } });
      }
    }, 100); // 100ms delay to ensure cleanup completes
  };

  // Effect to attach remote video elements to the DOM
  useEffect(() => {
    // Attach remote video elements to their containers
    remoteVideoElementsRef.current.forEach((videoElement, participantId) => {
      if (videoElement && !videoElement.parentNode) {
        // Create a container for this participant if it doesn't exist
        const participantContainer = document.querySelector(
          `[data-participant-id="${participantId}"]`,
        );
        if (participantContainer) {
          participantContainer.appendChild(videoElement);
          console.log(
            "🔄 Attached video element for participant:",
            participantId,
          );
        }
      }
    });
  }, [remoteParticipants, remoteVideoElementsRef]);

  // Get participant data
  const totalParticipants = remoteParticipants.size + 1; // +1 for local participant
  const participants = Array.from(remoteParticipants.values());

  // Only show additional slots when there are actually more than 2 participants
  const shouldShowAdditionalSlots = totalParticipants > 2;

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex flex-1 gap-6 h-full w-full relative">
        <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl overflow-hidden relative min-h-0 flex flex-col shadow-lg border border-gray-200">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">
                Connecting to video call...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex flex-1 gap-6 h-full w-full relative">
        <div className="flex-1 bg-gradient-to-br from-red-50 to-red-100 rounded-xl overflow-hidden relative min-h-0 flex flex-col shadow-lg border border-red-200">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-red-800 mb-2">
                Connection Failed
              </h3>
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={retryConnection}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 h-full w-full relative">
      {/* Multi-participant grid container */}
      <div
        className={`flex-1 grid gap-4 p-4 ${
          shouldShowAdditionalSlots
            ? totalParticipants <= 4
              ? "grid-cols-2 grid-rows-2"
              : "grid-cols-3"
            : "grid-cols-2"
        }`}
      >
        {/* Local participant (always first) */}
        <div
          className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl overflow-hidden shadow-lg border border-gray-200 min-h-0"
          ref={localRef}
        >
          {/* Video container */}
          <div className="absolute inset-0">
            {(!isVideoEnabled || !isLocalVideoAttached) && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-2xl md:text-4xl font-bold shadow-lg">
                  {localInitials}
                </div>
              </div>
            )}
          </div>

          {/* Participant name */}
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs md:text-sm font-medium">
            {localName}
          </div>

          {/* Participant type badge */}
          <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium text-white bg-green-500">
            You
          </div>
        </div>

        {/* Remote participants */}
        {participants.map((participant, index) => (
          <div
            key={participant.info?.participantId || index}
            className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl overflow-hidden shadow-lg border border-gray-200 min-h-0"
            data-participant-id={participant.info?.participantId}
          >
            {/* Video container */}
            <div className="absolute inset-0">
              {!participant.videoElement && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white text-2xl md:text-4xl font-bold shadow-lg">
                    {participant.info?.initials || "?"}
                  </div>
                </div>
              )}
            </div>

            {/* Participant name */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs md:text-sm font-medium">
              {participant.info?.name || "Participant"}
            </div>

            {/* Participant type badge */}
            <div
              className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium text-white ${
                participant.info?.type === "doctor"
                  ? "bg-blue-500"
                  : participant.info?.type === "patient"
                    ? "bg-purple-500"
                    : "bg-gray-500"
              }`}
            >
              {participant.info?.type === "doctor"
                ? "Doctor"
                : participant.info?.type === "patient"
                  ? "Patient"
                  : "Guest"}
            </div>
          </div>
        ))}

        {/* Empty slots for future participants - only show when there are already 3+ participants */}
        {shouldShowAdditionalSlots &&
          totalParticipants < maxParticipants &&
          Array.from(
            { length: Math.max(0, maxParticipants - totalParticipants) },
            (_, index) => (
              <div
                key={`empty-${index}`}
                className="relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl overflow-hidden shadow-lg border border-gray-300 min-h-0 opacity-50"
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 md:w-24 md:h-24 rounded-full bg-gray-400 flex items-center justify-center text-white text-lg md:text-2xl font-bold">
                    +
                  </div>
                </div>
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-30 text-white px-2 py-1 rounded-full text-xs font-medium">
                  Available
                </div>
              </div>
            ),
          )}
      </div>
      {/* Controls Bar */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-800 bg-opacity-90 backdrop-blur-sm rounded-full p-3 flex space-x-3 z-10 shadow-lg">
        {/* Mute/Unmute Button */}
        <button
          onClick={toggleAudio}
          className={`p-4 rounded-full text-white transition-all duration-200 hover:scale-105 ${
            isAudioEnabled
              ? "bg-gray-600 hover:bg-gray-500"
              : "bg-red-600 hover:bg-red-700"
          }`}
          title={isAudioEnabled ? "Mute Microphone" : "Unmute Microphone"}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {isAudioEnabled ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5.586 15H4a1 1 0 01-1-1v-3a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
              />
            )}
          </svg>
        </button>
        {/* Video On/Off Button */}
        <button
          onClick={toggleVideo}
          className={`p-4 rounded-full text-white transition-all duration-200 hover:scale-105 ${
            isVideoEnabled
              ? "bg-gray-600 hover:bg-gray-500"
              : "bg-red-600 hover:bg-red-700"
          }`}
          title={isVideoEnabled ? "Turn Off Camera" : "Turn On Camera"}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {isVideoEnabled ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18 12M5.636 5.636L12 12"
              />
            )}
          </svg>
        </button>
        {/* End Call Button */}
        <button
          onClick={handleLeaveCall}
          className="p-4 rounded-full bg-red-600 text-white hover:bg-red-700 transition-all duration-200 hover:scale-105"
          title="End Call"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z"
            />
          </svg>
        </button>
        {/* Add Participant Button */}
        {addparticipant && <AddParticipantToCall />}
        {toggleFormsPanel && typeof toggleFormsPanel === "function" && (
          <button
            onClick={toggleFormsPanel}
            className="p-4 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200 hover:scale-105"
            title="Toggle Medical Forms"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
